import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { Badge, Button, Card, Field, Input, Select, Textarea, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber, refusalMessage, type PartGridDTO } from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { StockTransferClient, type StockTransferResult } from "../../api/stockCount";
import { NexpimView } from "../../views";
import { PartPicker } from "./PartPicker";
import { localToday } from "./StockAdjustmentForm";

interface Line {
  part_id: number;
  number: string;
  name: string;
  unit: string | null;
  quantity: string;
}

/**
 * Stock moved from one ledger warehouse to another — MEGA restocking the
 * service shelf, material sent to a subcontractor and back.
 *
 * <p>One TRANSFER document: per part, a movement out of the source and one
 * into the destination, so the pair is never half-posted. Distribution
 * warehouses are not offered: their stock is Nexera's. Taking the source below
 * zero is allowed, as everywhere in the ledger; the result shows where both
 * ends now stand.</p>
 */
export function StockTransferPage() {
  const { t } = useTranslation();
  const { http, hasView, canWrite } = useAuth();
  const queryClient = useQueryClient();
  const allowed = hasView(NexpimView.MANUFACTURING_STOCK);
  const transfers = useMemo(() => new StockTransferClient(http), [http]);

  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
    enabled: allowed,
  });
  const ledgerWarehouses = (warehouses.data ?? []).filter((w) => w.is_active && w.kind !== "DISTRIBUTION");
  const options = ledgerWarehouses.map((w) => ({ value: String(w.id), label: `${w.code} · ${w.name ?? ""}` }));

  const [sourceId, setSourceId] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const effectiveSource = sourceId || String(ledgerWarehouses.find((w) => w.kind === "MANUFACTURING")?.id ?? "");
  const [date, setDate] = useState(localToday);
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [lines, setLines] = useState<Line[]>([]);
  const [result, setResult] = useState<StockTransferResult | null>(null);

  const addPart = (part: PartGridDTO) =>
    setLines((existing) =>
      existing.some((line) => line.part_id === part.id)
        ? existing
        : [...existing, { part_id: part.id, number: part.number, name: part.name, unit: part.stock_unit_code, quantity: "" }],
    );
  const setQuantity = (partId: number, quantity: string) =>
    setLines((existing) => existing.map((line) => (line.part_id === partId ? { ...line, quantity } : line)));

  // Every bad line is marked, not only the first one found.
  const isInvalid = (line: Line) => !(Number(line.quantity) > 0);
  const invalidCount = lines.filter(isInvalid).length;
  const sameWarehouse = effectiveSource !== "" && effectiveSource === destinationId;
  const ready =
    canWrite && effectiveSource !== "" && destinationId !== "" && !sameWarehouse && lines.length > 0 && invalidCount === 0;
  // Say why Post is greyed out rather than leaving the user to guess.
  const blocker = !canWrite
    ? null
    : destinationId === ""
      ? t("stock-transfer.need-destination", "Choose where the stock goes.")
      : lines.length === 0
        ? t("stock.adjust.need-lines", "Add at least one part.")
        : invalidCount > 0
          ? t("stock-transfer.need-quantity", "Every line needs a quantity above zero.")
          : null;

  const post = useMutation({
    mutationFn: () =>
      transfers.transfer({
        source_warehouse_id: Number(effectiveSource),
        destination_warehouse_id: Number(destinationId),
        document_date: date || null,
        reference: reference.trim() || null,
        note: note.trim() || null,
        lines: lines.map((line) => ({ part_id: line.part_id, quantity: Number(line.quantity) })),
      }),
    onSuccess: async (posted) => {
      setResult(posted);
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
      toast.success(t("stock-transfer.posted", "Transfer #{{id}} posted", { id: posted.stock_document_id }));
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  const codeOf = (id: string) => ledgerWarehouses.find((w) => String(w.id) === id)?.code ?? "";

  return (
    <>
      <PageHeader
        parent={{ to: "/manufacturing/stock", label: t("navigation.item.stock", "Stock") }}
        title={t("stock-transfer.title", "Transfer between warehouses")}
        subtitle={t("stock-transfer.subtitle", "Out of one warehouse and into another, in one document.")}
      />
      <div className="p-4 lg:p-6">
        {!allowed ? (
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        ) : result ? (
          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-3">
              <p className="text-[13px] text-ink">
                {t("stock-transfer.done", "Document #{{id}} posted: {{from}} → {{to}}.", {
                  id: result.stock_document_id,
                  from: codeOf(effectiveSource),
                  to: codeOf(destinationId),
                })}
              </p>
              <table className="w-full text-[13px] tabular-nums">
                <thead className="text-left text-[11px] uppercase tracking-wide text-ink-faint">
                  <tr>
                    <th className="py-1">{t("column.part.number", "Number")}</th>
                    <th className="py-1 text-right">{t("stock-transfer.moved", "Moved")}</th>
                    <th className="py-1 text-right">{t("stock-transfer.source-after", "{{code}} now", { code: codeOf(effectiveSource) })}</th>
                    <th className="py-1 text-right">{t("stock-transfer.destination-after", "{{code}} now", { code: codeOf(destinationId) })}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.lines.map((line) => (
                    <tr key={line.part_number} className="border-t border-line">
                      <td className="py-1">{line.part_number}</td>
                      <td className="py-1 text-right">{formatNumber(line.quantity)}</td>
                      <td className={"py-1 text-right " + (line.source_balance_after < 0 ? "text-risk" : "")}>
                        {formatNumber(line.source_balance_after)}
                      </td>
                      <td className="py-1 text-right font-medium">{formatNumber(line.destination_balance_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setResult(null);
                    setLines([]);
                    setReference("");
                    setNote("");
                  }}
                >
                  {t("stock-transfer.another", "Post another transfer")}
                </Button>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-4 sm:p-5">
            <div className="flex flex-col gap-4">
              <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
                <Field label={t("stock-transfer.source", "From")} htmlFor="transfer-source" required>
                  <Select
                    id="transfer-source"
                    value={effectiveSource}
                    options={options}
                    onChange={(event) => {
                      setSourceId(event.target.value);
                      // The destination list drops the new source; a destination
                      // equal to it would show as an unknown entry.
                      if (event.target.value === destinationId) setDestinationId("");
                    }}
                  />
                </Field>
                <ArrowRight className="mb-2.5 hidden size-4 text-ink-faint lg:block" />
                <Field
                  label={t("stock-transfer.destination", "To")}
                  htmlFor="transfer-destination"
                  required
                  error={sameWarehouse ? t("stock-transfer.same", "Choose a different warehouse.") : undefined}
                >
                  <Select
                    id="transfer-destination"
                    value={destinationId}
                    placeholder={t("form.choose", "Choose…")}
                    options={options.filter((option) => option.value !== effectiveSource)}
                    onChange={(event) => setDestinationId(event.target.value)}
                  />
                </Field>
                <span className="hidden lg:block" />
                <Field label={t("form.field.date", "Date")} htmlFor="transfer-date">
                  <Input id="transfer-date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
                </Field>
              </div>
              <Field label={t("form.field.reference", "Reference")} htmlFor="transfer-reference" hint={t("stock-transfer.reference.hint", "A bill of lading or slip number.")}>
                <Input id="transfer-reference" value={reference} onChange={(event) => setReference(event.target.value)} />
              </Field>

              {/* The picker sits outside the scrolling box: inside it, its list
                  of matches was clipped to the height of the table. */}
              <div className="rounded-md border border-line">
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="bg-surface-sunk text-left text-[11px] uppercase tracking-wide text-ink-faint">
                      <tr>
                        <th className="px-3 py-2">{t("column.part.number", "Number")}</th>
                        <th className="px-3 py-2 text-right">{t("stock-transfer.quantity", "Quantity")}</th>
                        <th className="px-3 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr key={line.part_id} className="border-t border-line">
                          <td className="px-3 py-1.5">
                            <span className="font-medium">{line.number}</span>
                            <span className="ml-2 text-[11px] text-ink-faint">{line.name}</span>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <span className="inline-flex items-center gap-1.5">
                              <Input
                                type="number"
                                step="any"
                                min="0"
                                value={line.quantity}
                                onChange={(event) => setQuantity(line.part_id, event.target.value)}
                                className="h-8 w-28 text-right"
                                invalid={line.quantity !== "" && isInvalid(line)}
                              />
                              <span className="w-10 text-left text-[11px] text-ink-faint">{line.unit ?? ""}</span>
                            </span>
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <button
                              type="button"
                              aria-label={t("button.remove", "Remove")}
                              className="text-ink-faint hover:text-risk"
                              onClick={() => setLines((existing) => existing.filter((item) => item.part_id !== line.part_id))}
                            >
                              <Trash2 className="size-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border-t border-line px-3 py-2">
                  <PartPicker onPick={addPart} exclude={lines.map((line) => line.part_id)} />
                </div>
              </div>

              <Field label={t("form.field.note", "Note")} htmlFor="transfer-note">
                <Textarea id="transfer-note" rows={2} value={note} onChange={(event) => setNote(event.target.value)} />
              </Field>

              <div className="flex items-center gap-3">
                <Button variant="primary" disabled={!ready} loading={post.isPending} onClick={() => post.mutate()}>
                  {t("stock-transfer.post", "Post transfer")}
                </Button>
                {lines.length > 0 && (
                  <Badge variant="neutral" size="sm">
                    {t("stock.lines", "{{n}} lines", { n: lines.length })}
                  </Badge>
                )}
                {!canWrite && (
                  <span className="text-[12px] text-ink-muted">{t("stock.read-only", "Your account can view stock but not adjust it.")}</span>
                )}
                {blocker && <span className="text-[12px] text-ink-muted">{blocker}</span>}
              </div>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
